/**
 * Auth Context
 *
 * React context provider that initializes authentication on mount
 * and provides auth state/actions throughout the app.
 *
 * Wraps the Zustand store for React integration.
 *
 * Session keep-alive:
 * - While signed in, calls POST /api/auth/refresh every 15 minutes so the
 *   server slides the session expiry (no token reaches the browser).
 * - Auto-refreshes token every 5 minutes
 * - Clears token on logout
 */

import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../state_management_and_stores/auth.store.js';
import { skipsPermissionChecks } from '@aiga/app-config/permissions';
import { authService, type User, type Permission, type Role } from '../services/auth.service.js';

interface AuthContextValue {
  // State
  user: User | null;
  permissions: Permission[];
  role: Role | null;
  passwordChangeRequired: boolean; // True if user needs to set a real password
  isLoading: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean; // Computed: user is logged in
  error: string | null;

  // Actions
  initializeAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  clearError: () => void;

  // Helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth Provider
 *
 * Initializes authentication state from session cookie on mount.
 * Provides auth context to all child components.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const {
    user,
    permissions,
    role,
    passwordChangeRequired,
    isLoading,
    isInitialized,
    error,
    initializeAuth,
    login: storeLogin,
    logout: storeLogout,
    refreshPermissions,
    clearError,
    setUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
  } = useAuthStore();

  const queryClient = useQueryClient();

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Keep the server session alive while signed in (2026-10-02). The server
  // slides a session's 30-day expiry when it rotates the stored Supabase
  // refresh token; POST /auth/refresh does that and returns no token. This
  // replaced the Realtime token poll, which handed the browser a database
  // credential and powered no feature (see docs/SECURITY.md "Direct database
  // access").
  useEffect(() => {
    if (!user || !isInitialized) return;
    const KEEP_ALIVE_MS = 15 * 60 * 1000;
    const keepAlive = () => {
      authService.refresh().catch(() => {
        // A 401 here is handled by the app's session-expiry flow; other
        // failures are retried on the next tick.
      });
    };
    keepAlive();
    const intervalId = setInterval(keepAlive, KEEP_ALIVE_MS);
    return () => clearInterval(intervalId);
  }, [user, isInitialized]);

  // Wrap login (the keep-alive effect above starts once the user is set)
  const login = useCallback(
    async (email: string, password: string) => {
      await storeLogin(email, password);
    },
    [storeLogin]
  );

  // Wrap logout to clear the query cache.
  //
  // Client state is cleared FIRST — before any awaited network call — so the
  // SessionExpirationHandler redirect path works even when the logout POST
  // fails (network error, CSRF refusal, aborted by queryClient.cancelQueries).
  // If setUser(null) is skipped, isAuthenticated stays true and any redirect
  // can bounce the user right back to /dashboard, causing a session-expired
  // loop.
  const logout = useCallback(async () => {
    setUser(null);
    queryClient.clear();

    try {
      await storeLogout();
    } catch (error) {
      console.warn('[Auth] Server logout failed (state already cleared):', error);
    }
  }, [storeLogout, setUser, queryClient]);

  const value: AuthContextValue = {
    user,
    permissions,
    role,
    passwordChangeRequired,
    isLoading,
    isInitialized,
    isAuthenticated: !!user, // User is authenticated if user object exists
    error,
    initializeAuth,
    login,
    logout,
    refreshPermissions,
    clearError,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * PreviewAuthProvider — re-provides the auth context so everything under it answers as `role` with `permissions`
 * (the Dashboard designer, pages/DashboardDesignerPage.tsx). Every permission check reads useAuth()
 * (useFeatureAccess, useWidgetAccess, useSkipsPermissionChecks, useIsSuperAdmin, WidgetSlot), so they all follow.
 *
 * The signed-in user is kept for its id and name (the cards still load the admin's own rows: the server has no
 * "act as role"), but with no email and isAdmin false, so the AIG-staff and super-admin-email bypasses of the
 * person previewing cannot leak in. A Super Admin role still skips checks by its name, as it does for real.
 * The helpers check the given list only; the actions are the real ones.
 *
 * `user` replaces the signed-in user, under the same rule (widgets/preview/WidgetPreview: an invented person, whose
 * samples load instead of anyone's rows).
 */
export function PreviewAuthProvider({
  role,
  permissions,
  user: previewUser,
  children,
}: {
  role: Role;
  permissions: Permission[];
  user?: User;
  children: React.ReactNode;
}) {
  const real = useAuth();
  const value = useMemo<AuthContextValue>(() => {
    const shown = previewUser ?? real.user;
    const user: User | null = shown ? { ...shown, email: '', isAdmin: false, isAdminVerified: false } : null;
    const has = (name: string) =>
      skipsPermissionChecks({ role: role.name, email: null, isAdmin: false }) || permissions.some((p) => p.name === name);
    return {
      ...real,
      user,
      role,
      permissions,
      hasPermission: has,
      hasAnyPermission: (names) => names.some(has),
      hasAllPermissions: (names) => names.every(has),
      isAdmin: () => false,
    };
  }, [real, role, permissions, previewUser]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 *
 * Must be used within AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
